// src/components/EventForm/LocationSection.js
import React from 'react';
import styled from 'styled-components';
import GooglePlacesAutocomplete from '../GooglePlacesAutocomplete';
import InputText from '../../components/Input/InputText';
import Button from '../Button/Button';
import LocationIcon from '../../components/Icons/LocationIcon';
import SearchIcon from '../../components/Icons/SearchIcon';
import CloseIcon from '../../components/Icons/CloseIcon';
import Typography from '../../components/Typography';
import EventMap from '../../components/EventMap';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import InfoCircleOutlineIcon from '../Icons/InfoCircleOutlineIcon';
import Tooltip from '../Tooltip/Tooltip';

const LocationSection = ({
  location,
  onLocationChange,
  onPlaceChanged,
  onClearLocation,
  autocompleteRef,
  coordinates,
  error
}) => {
  const { t } = useTranslation('createEvent');
  return (
    <Wrapper>
      <SectionHeader>
        <LocationIcon fill={theme.colors.defaultStrong} />
        <Typography as="label" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
          {t('eventForm.location.heading')}
        </Typography>
        <Tooltip text={t('eventForm.location.tooltip')} maxWidth='230px' style={{ marginLeft: '-4px' }}>
          <InfoButton>
            <InfoCircleOutlineIcon width="20px" height="20px" fill={theme.colors.defaultSubtle} />
          </InfoButton>
        </Tooltip>
      </SectionHeader>
      <InputWrapper>
        <SearchIconStyled fill={theme.colors.defaultSubtle} />
        <GooglePlacesAutocomplete
          onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
          onPlaceChanged={onPlaceChanged}
        >
          <InputFieldWrapper>
            <StyledInput
              size="large"
              type="text"
              name="location"
              value={location}
              onChange={onLocationChange}
              placeholder={t('eventForm.location.searchPlaceholder')}
              $variant={error ? 'error' : ''}
              required
            />
            {Boolean(location) && (
              <ClearInputButton
                type="button"
                $variant="ghost"
                size="m"
                sizeMobile="s"
                $iconOnly
                aria-label={t('eventForm.location.clear')}
                onClick={onClearLocation}
              >
                <CloseIcon width={16} height={16} fill={theme.colors.defaultWeak} />
              </ClearInputButton>
            )}
          </InputFieldWrapper>
        </GooglePlacesAutocomplete>
        {error &&
          <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
            {error}
          </Typography>
        }
      </InputWrapper>

      {coordinates && coordinates.lat && coordinates.lng && (
        <MapWrapper>
          <EventMap
            lat={coordinates.lat}
            lng={coordinates.lng}
            maxHeight="200px"
            mobileHeight="200px"
            borderRadius="8px"
          />
        </MapWrapper>
      )}
    </Wrapper>
  );
};

export default LocationSection;

const Wrapper = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm} 0;
`;

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  position: relative;
  width: 100%;
`;

const StyledInput = styled(InputText)`
  background: ${({ theme }) => theme.fill.defaultSubtle};
  height: ${({ theme }) => theme.sizing.xl};
  padding-left: 36px;
  padding-right: 36px;

  &:hover {
    background: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const SearchIconStyled = styled(SearchIcon)`
  left: ${({ theme }) => theme.sizing.xs};
  position: absolute;
  top: 10px;
  z-index: 1;
`;

const InputFieldWrapper = styled.div`
  position: relative;
`;

const ClearInputButton = styled(Button)`
  position: absolute;
  right: ${({ theme }) => theme.sizing.xs};
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
`;

const MapWrapper = styled.div`
  margin-top: ${({ theme }) => theme.sizing.xs};
`;

const InfoButton = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: ${({ theme }) => theme.sizing.md};
  justify-content: center;
  transition: background-color 0.3s ease-in-out;
  width: ${({ theme }) => theme.sizing.md};

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;
